import { CurrencyCode } from '@noths/polaris-dev-ts-types';
import dynamic from 'next/dynamic';

import { CatalogServiceProductData, getProductsFromCatalogService } from 'src/services/catalog';
import { trackCarouselNavigation } from 'src/tracking/common/carousel';
import { trackProductClick, trackProductImpression } from 'src/tracking/common/products';
import { transformProductModelToProductForTracking } from 'src/tracking/common/productTransformer';
import { ContentBlockRenderer } from 'src/types/Block';
import { ContentstackSection } from 'src/types/Contentstack';
import { PlacementContext } from 'src/types/PlacementContext';
import { AsyncTransformer } from 'src/types/Transformer';
import { ProductCarouselProps } from '../../../components/blocks/ProductCarouselBlock';
import { transformProductModelToProductCarouselProduct } from './transformer';
import { getProductsValidationErrors } from './validation';

export interface ContentstackProductCarousel extends ContentstackSection {
  heading: string;
  products: { product_code: string }[];
  tracking_title: string;
}

interface ProductCarouselModel extends Omit<ProductCarouselProps, 'products'> {
  products: CatalogServiceProductData[];
}

interface ProductCarouselSerializedData extends ProductCarouselModel {
  pageType: string;
  trackingTitle: string;
  trackingPosition?: number;
  currency: CurrencyCode;
}

const hideNonPurchasableProducts = (products: CatalogServiceProductData[]) =>
  products.filter((product) => product.purchasable);

/* istanbul ignore next */
export const renderer: ContentBlockRenderer<ProductCarouselSerializedData, ProductCarouselProps> = {
  Component: dynamic<ProductCarouselProps>(() =>
    import('src/components/blocks/ProductCarouselBlock').then((mod) => mod.ProductCarouselBlock),
  ),
  getProps: ({ ...serializedData }) => {
    const {
      currency,
      pageType,
      products,
      trackingPosition,
      trackingTitle,
      ...restOfSerializedData
    } = serializedData;
    const dataForTracking: PlacementContext = {
      placementContentType: 'Product carousel',
      trackingPosition,
      trackingTitle,
    };
    const renderedProducts = hideNonPurchasableProducts(products);

    return {
      ...restOfSerializedData,
      products: renderedProducts.map((product) =>
        transformProductModelToProductCarouselProduct(product, { currency }),
      ),
      onVisible: () => {
        if (renderedProducts.length > 0) {
          trackProductImpression({
            ...dataForTracking,
            pageType,
            products: renderedProducts.map(transformProductModelToProductForTracking),
          });
        }
      },
      onProductClick: (e: React.MouseEvent<HTMLAnchorElement>, { index }: { index: number }) => {
        if (renderedProducts.length > 0) {
          trackProductClick(e, {
            ...dataForTracking,
            pageType,
            product: transformProductModelToProductForTracking(renderedProducts[index]),
            productIndex: index,
            numberOfItems: renderedProducts.length,
          });
        }
      },
      onNavigation: (swiped, control) => {
        const trackingDirection = control === 'next' ? 'next' : 'previous';

        trackCarouselNavigation(swiped, trackingDirection, {
          ...dataForTracking,
          numberOfItems: renderedProducts.length,
        });
      },
    };
  },
};

const getProductCodes = (products: { product_code: string }[]) =>
  products.map(({ product_code }) => Number(product_code));

export const transformer: AsyncTransformer<
  ContentstackProductCarousel,
  ProductCarouselSerializedData,
  ProductCarouselProps
> = async ({ _metadata, heading, products, tracking_title }, { currency, pageType }) => {
  /* istanbul ignore next */
  const catalogServiceProducts = await getProductsFromCatalogService(getProductCodes(products));
  const renderableProducts = (catalogServiceProducts || []).filter(
    (product) => product.purchasable,
  );
  const errors = getProductsValidationErrors(
    getProductCodes(products),
    catalogServiceProducts,
    renderableProducts,
  );

  return {
    uid: _metadata.uid,
    renderer,
    serializedData: {
      title: heading,
      products: renderableProducts,
      currency,
      pageType,
      trackingTitle: tracking_title,
    },
    errors,
  };
};
